<template>
  <div class="w-full chart-container"
    :style="{'min-height': minHeigth}"
    >
    <div class="chart-a11y-wrapper" :class="{'chart-focused': focusedBarIndex >= 0}">
      <canvas :id="id" aria-hidden="true"></canvas>
    </div>
    <div v-if="chardataFullstack && chardataFullstack.length"
      class="sr-only"
      role="listbox"
      :aria-label="labelUsed"
      :aria-roledescription="$t('charts.aria_roledescription')"
      @keydown="handleKeydown"
    >
      <div
        v-for="(item, index) in chardataFullstack"
        :key="index"
        :id="id + '-bar-' + index"
        role="option"
        :tabindex="getBarTabindex(index)"
        :aria-selected="focusedBarIndex === index"
        @focus="onOptionFocus(index)"
        @blur="onOptionBlur($event)"
      >{{ (index + 1) + ' de ' + chardataFullstack.length + ': ' + item.nombre + ', ' + labelUsed + ': ' + item.numUtilizado + ', ' + labelTotal + ': ' + item.numTotal }}</div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'

export default {
  name: 'Chart',
  props: {
    type: {
      type: String,
      default: 'bar'
    },
    id: {
      type: String,
      default: 'chart'
    },
    labelUsed: {
      type: String,
      default: 'Utilizado'
    },
    labelTotal: {
      type: String,
      default: 'Total'
    },
    chardataFullstack: {
      type: [Array, Object]
    },
    colorWarning: {
      type: String,
      default: '#ff8896'
    },
    // colorImportant: {
    //   type: String,
    //   default: '#007FFF'
    // },
    limitWarning: {
      type: Number,
      default: 60
    },
    limitImportant: {
      type: Number,
      default: 80
    }
  },
  data () {
    return {
      ChartData: null,
      myChart: null,
      focusedBarIndex: -1,
      colorPrimary: this.getColor('--primary-300'),
      colorSecondary: '#ffffff',
      colorImportant: this.getColor('--primary-DEFAULT'),
      colorSecondaryDark: this.getColor('--darkblue-DEFAULT'),
      colorFont: this.getColor('--secondary-DEFAULT'),
      colorFontDark: this.getColor('--secondary-100'),
      colorGridLinesDark: this.getColor('--darkblue-400'),
      colorBorder: this.getColor('--primary-600'),
    }
  },
  computed: {
    minHeigth () {
      let heigth = 150
      if (this.chardataFullstack) {
        const actualHeightAux = (this.chardataFullstack.length + 1) * 28
        if (actualHeightAux > heigth) {
          heigth = actualHeightAux
        }
      }
      return heigth + 'px'
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    infoResumenTexto () {
      var msg = ''
      if (this.chardataFullstack) {
        msg += 'Licenciamiento por entidades. '
        this.chardataFullstack.forEach(x => {
          msg += '' + x.nombre + ': ' + x.numUtilizado + ' de '+ x.numTotal + '; '
        })
      }
      return msg
    },
    infoResumenTextoFormat () {
      var msg = ''
      if (this.chardataFullstack) {
        msg += '<b>Licenciamiento por entidades</b><br/>'
        this.chardataFullstack.forEach(x => {
          msg += '' + x.nombre + ': ' + x.numUtilizado + ' de '+ x.numTotal + '<br/>'
        })
      }
      return msg
    }
  },
  watch: {
    chardataFullstack: 'dataChangeHandler',
    darkMode: 'changeDarkmode'
  },
  methods: {
    getColor (property) {
      return getComputedStyle(document.querySelector(':root')).getPropertyValue(property)
    },
    getBarTabindex (index) {
      if (this.focusedBarIndex >= 0) return index === this.focusedBarIndex ? 0 : -1
      return index === 0 ? 0 : -1
    },
    focusBarOption (index) {
      const el = document.getElementById(this.id + '-bar-' + index)
      // preventScroll evita el salto nativo del navegador (las opciones sr-only
      // están al principio del contenedor); el centrado lo hace scrollBarIntoView.
      if (el) el.focus({ preventScroll: true })
    },
    getScrollParent (node) {
      let el = node ? node.parentElement : null
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el)
        const overflowY = style.overflowY
        if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && el.scrollHeight > el.clientHeight) {
          return el
        }
        el = el.parentElement
      }
      return null
    },
    scrollBarIntoView (index) {
      // WCAG 2.1 AA (2.4.7 Focus Visible): al enfocar una barra por teclado la
      // centramos en pantalla para que sea visible aunque el gráfico sea largo.
      if (!this.myChart) return
      const meta = this.myChart.getDatasetMeta(0)
      const bar = meta && meta.data ? meta.data[index] : null
      if (!bar) return
      const point = typeof bar.getCenterPoint === 'function' ? bar.getCenterPoint() : (bar._model || bar._view)
      const canvas = this.myChart.canvas
      if (!point || !canvas) return
      const barViewportY = canvas.getBoundingClientRect().top + point.y
      const scroller = this.getScrollParent(this.$el)
      if (scroller) {
        const scrollerRect = scroller.getBoundingClientRect()
        scroller.scrollBy({ top: barViewportY - (scrollerRect.top + scroller.clientHeight / 2), behavior: 'smooth' })
      } else {
        window.scrollBy({ top: barViewportY - (window.innerHeight / 2), behavior: 'smooth' })
      }
    },
    onOptionFocus (index) {
      this.focusedBarIndex = index
      this.highlightBar(index)
      this.scrollBarIntoView(index)
    },
    onOptionBlur (event) {
      const related = event.relatedTarget
      if (related && related.id && related.id.startsWith(this.id + '-bar-')) {
        return
      }
      this.clearHighlight()
      this.focusedBarIndex = -1
    },
    handleKeydown (event) {
      if (!this.myChart || !this.chardataFullstack || this.chardataFullstack.length === 0) return
      const totalBars = this.chardataFullstack.length
      let newIndex = this.focusedBarIndex
      switch (event.key) {
        case 'Escape':
        case 'Esc':
          if (this.focusedBarIndex >= 0) {
            event.preventDefault()
            event.stopPropagation()
            this.clearHighlight()
          }
          return
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault()
          newIndex = (this.focusedBarIndex + 1) % totalBars
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault()
          newIndex = (this.focusedBarIndex - 1 + totalBars) % totalBars
          break
        case 'Enter':
        case ' ':
          event.preventDefault()
          if (this.focusedBarIndex >= 0) {
            this.$emit('callback', this.chardataFullstack[this.focusedBarIndex].nombre)
          }
          return
        case 'Home':
          event.preventDefault()
          newIndex = 0
          break
        case 'End':
          event.preventDefault()
          newIndex = totalBars - 1
          break
        default:
          return
      }
      this.focusBarOption(newIndex)
    },
    highlightBar (index) {
      if (!this.myChart) return
      const meta0 = this.myChart.getDatasetMeta(0)
      const meta1 = this.myChart.getDatasetMeta(1)
      if (!meta0 || !meta0.data) return
      const dataset0 = this.myChart.data.datasets[0]
      const dataset1 = this.myChart.data.datasets[1]
      const totalBars = this.chardataFullstack.length
      const dimAlpha = '55'
      // Build per-bar colors for dataset 0 (used)
      const colors0 = []
      for (let i = 0; i < totalBars; i++) {
        const baseColor = this.chardataFullstack[i].porcentajeEnUso >= this.limitImportant ? this.colorImportant : this.colorPrimary
        colors0.push(i === index ? baseColor : baseColor + dimAlpha)
      }
      dataset0.backgroundColor = colors0
      // Build per-bar colors for dataset 1 (total/remaining)
      const baseColor1 = this.darkMode ? this.colorSecondaryDark : this.colorSecondary
      const colors1 = []
      for (let i = 0; i < totalBars; i++) {
        colors1.push(i === index ? baseColor1 : baseColor1 + dimAlpha)
      }
      dataset1.backgroundColor = colors1
      // Update colors first (no animation)
      this.myChart.update(0)
      // Show tooltip AFTER update so it doesn't get reset
      const activeElements = []
      if (meta0.data[index]) activeElements.push(meta0.data[index])
      if (meta1 && meta1.data && meta1.data[index]) activeElements.push(meta1.data[index])
      if (activeElements.length > 0) {
        this.myChart.tooltip._active = activeElements
        this.myChart.tooltip.update(true)
        this.myChart.draw()
      }
    },
    clearHighlight () {
      if (!this.myChart) return
      // Restore original colors via controlColor
      this.controlColor()
      const dataset1 = this.myChart.data.datasets[1]
      if (dataset1) {
        dataset1.backgroundColor = this.darkMode ? this.colorSecondaryDark : this.colorSecondary
      }
      this.myChart.update(0)
      this.myChart.tooltip._active = []
      this.myChart.tooltip.update(true)
      this.myChart.draw()
    },
    changeDarkmode: function () {
      Chart.defaults.global.defaultFontColor = this.darkMode ? this.colorFontDark : this.colorFont
      this.myChart.config.options.scales.xAxes.forEach(x => {
        x.gridLines.color = this.darkMode ? this.colorGridLinesDark : this.colorPrimary
      }) 
      this.myChart.config.options.scales.yAxes.forEach(x => {
        x.gridLines.color = this.darkMode ? this.colorGridLinesDark : this.colorPrimary
        x.gridLines.zeroLineColor = this.darkMode ? this.colorGridLinesDark : this.colorPrimary
      }) 
      this.myChart.config.data.datasets[0].backgroundColor = this.colorPrimary
      this.myChart.config.data.datasets[1].backgroundColor = this.darkMode ? this.colorSecondaryDark : this.colorSecondary
      this.myChart.config.options.legend.labels.fontColor = this.darkMode ? this.colorFontDark : this.colorFont
      this.updateFixChart()
    },
    updateFixChart () {
      this.controlColor()
      this.myChart.update()
      // console.log(this.myChart)
      setTimeout(() => {
        this.myChart.update()
        this.$emit('chartUpdated', this.infoResumenTextoFormat)
      }, 200)
    },
    controlColor () {
      const colors = []
      this.myChart.config.data.datasets[0].data.forEach(x => {
        if (x >= this.limitImportant) colors.push(this.colorImportant)
        else colors.push(this.colorPrimary)
      })
      this.myChart.config.data.datasets[0].backgroundColor = colors
      // console.log(this.myChart.config.options.legend)
    },
    removeData () {
      this.myChart.data.labels = []
      // this.myChart.data.datasets.forEach(x => x.data=[])
      for (let index = 0; index < this.myChart.data.datasets.length; index++) {
        this.myChart.data.datasets[index].data = []
      }
      // this.myChart.data.labels.pop()
      // this.myChart.data.datasets.forEach(x => x.data.pop())
      this.updateFixChart()
    },
    addData (label, data) {
      this.myChart.data.labels.push(label)
      this.myChart.data.datasets.forEach(x => x.data.push(data))
      this.updateFixChart()
    },
    dataChangeHandler: function () {
      this.focusedBarIndex = -1
      if (this.myChart !== null && this.myChart !== undefined) {
        this.removeData()
      } 
      Chart.defaults.global.defaultFontColor = this.darkMode ? this.colorFontDark : this.colorFont
      const ctx = document.getElementById(this.id)
      const labels = []
      const values = []
      const valuesTotal = []
      this.chardataFullstack.forEach(element => {
        labels.push(element.nombre)
        values.push(element.porcentajeEnUso)
        valuesTotal.push(element.porcentajeRestante)
      })
      this.ChartData = null
      const self = this
      this.ChartData = {
        type: this.type, // 'horizontalBar',
        data: {
          labels: labels,
          datasets: [
            {
              label: this.labelUsed,
              data: values,
              backgroundColor: this.colorPrimary,
              borderColor: this.colorBorder,
              borderWidth: 2,
              maxBarThickness: 40,
              fontSize: 40,
            },
            {
              label: this.labelTotal,
              data: valuesTotal,
              backgroundColor: this.darkMode ? this.colorSecondaryDark : this.colorSecondary,
              borderColor: this.colorBorder,
              borderWidth: 2,
              maxBarThickness: 40,
              fontSize: 40,
            }
          ]
        },
        options: {
          legend: {
            display: false,
            onClick: null,
            labels: {
              fontColor: this.darkMode ? this.colorFontDark : this.colorFont,
              fontSize: 14,
              backgroundColor: this.colorPrimary,
            }
          },
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                var label = data.datasets[tooltipItem.datasetIndex].label || ''
                if (label) {
                  if (label === self.labelUsed) {
                    label += ': ' + self.chardataFullstack[tooltipItem.index].numUtilizado + ''
                  } else {
                    label += ': ' + self.chardataFullstack[tooltipItem.index].numTotal + ''
                  }
                }
                // label += Math.round(tooltipItem.yLabel * 100) / 100
                return label
              },
              title: function (tooltipItems, data) {
                return data.labels[tooltipItems[0].index]
              }
            }
          },
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              stacked: true,
              gridLines: {
                color: this.darkMode ? this.colorGridLinesDark : this.colorSecondary,
              },
              ticks: {
                precision: 0,
                fontSize: 14,
                callback: function (value, index, values) {
                  if (index === 0) return '%'
                  return value
                }
              }
            }],
            yAxes: [{
              stacked: true,
              gridLines: {
                color: this.darkMode ? this.colorGridLinesDark : this.colorSecondary,
                zeroLineColor: this.darkMode ? this.colorGridLinesDark : this.colorSecondary
              },
              ticks: {
                beginAtZero: true,
                fontSize: 14,
                callback: function (value, index, values) {
                  return value.length > 12 ? value.substr(0, 12) + '..' : value
                }
              }
            }]
          },
          onHover: (event, chartElement) => {
            const target = event.native ? event.native.target : event.target
            target.style.cursor = chartElement[0] ? 'pointer' : 'default'
          }
        }
      }
      // eslint-disable-next-line no-new
      const myChart = new Chart(ctx, this.ChartData)
      this.myChart = myChart
      ctx.onclick = function (evt) {
        const activePoint = myChart.getElementAtEvent(evt)[0]
        if (activePoint && activePoint._chart) {
          const data = activePoint._chart.data
          const datasetIndex = activePoint._datasetIndex
          const label = data.labels[activePoint._index]
          const value = data.datasets[datasetIndex].data[activePoint._index]
          console.log(label, value)
          self.$emit('callback', label)
        }
      }
      this.changeDarkmode()
    }
  }
}
</script>

<style scoped>

.chart-container {
  position: relative;
  height: 22vh;
}

.chart-a11y-wrapper {
  width: 100%;
  height: 100%;
}

.chart-a11y-wrapper.chart-focused {
  outline: 3px solid var(--primary-600);
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
